import * as Sentry from "@sentry/react";
import { getCurrentApplicationId } from "~/lib/applicationId";
import { useAuthStore } from "~/stores/useAuthStore";

type SentryBaseContext = {
  user_id: string | null;
  application_id: string;
  description: string;
  timestamp: string;
  [key: string]: unknown;
};

const buildBaseContext = (description: string): SentryBaseContext => ({
  user_id: useAuthStore.getState().userId,
  application_id: getCurrentApplicationId(),
  description,
  timestamp: new Date().toISOString(),
});

/**
 * プロジェクト共通のエラーログ送信。
 * user_id / application_id / error / description / timestamp を自動付与する。
 *
 * @param error - 発生したエラー（Error インスタンスまたは任意の値）
 * @param description - どの処理のどの箇所で失敗したか（例: "useXxx: API呼び出し失敗"）
 * @param context - 追加のコンテキスト情報（任意）
 */
export const captureError = (
  error: unknown,
  description: string,
  context?: Record<string, unknown>,
): void => {
  const baseContext = buildBaseContext(description);
  Sentry.withScope((scope) => {
    scope.setContext("error_info", { ...baseContext, ...context });
    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(String(error), "error");
    }
  });
};

/**
 * プロジェクト共通の Warning ログ送信。
 * user_id / application_id / description / timestamp を自動付与する。
 *
 * @param message - 警告メッセージ
 * @param description - どの処理のどの箇所で発生したか（例: "useXxx: 想定外の状態"）
 * @param context - 追加のコンテキスト情報（任意）
 */
export const captureWarning = (
  message: string,
  description: string,
  context?: Record<string, unknown>,
): void => {
  const baseContext = buildBaseContext(description);
  Sentry.withScope((scope) => {
    scope.setContext("error_info", { ...baseContext, ...context });
    Sentry.captureMessage(message, "warning");
  });
};
